/*
 * Copyright 2024 NewMedia Centre - Delft University of Technology
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

'use client'

// import dependencies
import { useState } from 'react'
import { useWebApp } from '@/hooks/webapp'
import { useRouter } from 'next/navigation'
import { slugify } from '@/utils/slug'

// import server actions
import { updateSlugAction } from '@/actions/webapps/update'
import { deleteAction } from '@/actions/webapps/delete'

// import components
import { Input } from '@/components/input'
import { DeleteButton, Button } from '@/components/buttons'
import {
	Field,
	Fieldset,
	Label,
	Legend
} from '@headlessui/react'

export const AdminSettings = () => {
	const router = useRouter()
	const webApp = useWebApp()

	// create state
	const [slug, setSlug] = useState(webApp.slug)

	// update slug action
	const updateSlug = async () => {
		await updateSlugAction({ id: webApp.id, slug })
		router.refresh()
	}

	// delete action
	const remove = async () => {
		await deleteAction({ id: webApp.id })
		router.push('/webapps')
	}

	return (
		<Fieldset className='flex flex-col gap-3 p-3'>
			<div className='flex flex-col'>
				<Legend className='text-xl font-semibold'>Manage Web App</Legend>
				<span className='text-xs font-light'>These actions cause breaking changes to this web app. Please ensure you know what you are doing.</span>
			</div>
			<Field as='form' className='flex items-stretch gap-5' action={updateSlug}>
				<div className='flex w-64 flex-col'>
					<Label className='font-semibold'>URL Slug</Label>
					<span className='text-xs font-light'>A unique, URL-friendly and easy-to-read name used to identify the web app. Changing the slug might cause other applications that depend on the API to stop working.</span>
				</div>
				<div className='flex grow items-start gap-3'>
					<Input required placeholder='Slug...' value={slug} onChange={e => setSlug(slugify(e.target.value))} />
					<Button type='submit'>Update</Button>
				</div>
			</Field>
			<form className='flex items-stretch gap-5' action={remove}>
				<div className='flex w-64 shrink-0 flex-col'>
					<span className='font-semibold'>Delete Web App</span>
					<span className='text-xs font-light'>This will permanently delete this web app. This cannot be undone.</span>
				</div>
				<DeleteButton type='submit'>Delete Web App</DeleteButton>
			</form>
		</Fieldset>
	)
}